import { type DateTime } from 'luxon';

import { Temporal } from '@willow/types-iso';

import { type AppContext } from '../GlobalExecutionContext';
import { murmurhash3_resource } from './murmurhash3';

/** Deployed environments that have Datadog telemetry. */
export type DatadogEnv = 'production' | 'staging' | 'flex';

const DATADOG_ENVS = new Set<string>(['production', 'staging', 'flex']);

export function isDatadogEnv(context: AppContext): context is DatadogEnv {
  return DATADOG_ENVS.has(context);
}

/** Any date-like value the codebase uses. Epoch-ms number, Date, Temporal.Instant, Temporal.ZonedDateTime, or Luxon DateTime. */
export type DatadogTimestamp = number | Date | Temporal.Instant | Temporal.ZonedDateTime | DateTime;

function toEpochMs(t: DatadogTimestamp): number {
  if (typeof t === 'number') return t;
  if (t instanceof Date) return t.getTime();
  if (t instanceof Temporal.Instant) return t.epochMilliseconds;
  if (t instanceof Temporal.ZonedDateTime) return t.toInstant().epochMilliseconds;
  // Luxon DateTime — has .toMillis() but isn't an instanceof we can check,
  // so this is the fallback for the remaining union member.
  return t.toMillis();
}

export interface DatadogUrlOptions {
  query: string;
  from?: DatadogTimestamp;
  to?: DatadogTimestamp;
}

/**
 * Builds a Datadog Logs Explorer URL for the given query.
 *
 * The query is URL-encoded automatically — pass the raw query string
 * (e.g. `env:production @resource:audit_logs_to_public_data_export_store`).
 */
export function datadogLogsUrl({ query, from, to }: DatadogUrlOptions): string {
  const params = new URLSearchParams({ query });

  if (from != null || to != null) {
    params.set('live', 'false');
    if (from != null) params.set('from_ts', String(toEpochMs(from)));
    if (to != null) params.set('to_ts', String(toEpochMs(to)));
  }

  return `https://app.datadoghq.com/logs?${params.toString()}`;
}

/**
 * Builds a Datadog APM Trace Explorer URL for the given query.
 *
 * The query is URL-encoded automatically — pass the raw query string
 * (e.g. `env:production operation_name:ResourceWatcher.tick resource_name:audit_logs`).
 */
export function datadogTracesUrl({ query, from, to }: DatadogUrlOptions): string {
  const params = new URLSearchParams({ query });

  if (from != null || to != null) {
    params.set('paused', 'true');
    if (from != null) params.set('start', String(toEpochMs(from)));
    if (to != null) params.set('end', String(toEpochMs(to)));
  }

  return `https://app.datadoghq.com/apm/traces?${params.toString()}`;
}

export interface DatadogResourceUrlOptions {
  /** Datadog service name (e.g. 'backend-server'). */
  service: string;
  /** Operation/span name (e.g. 'ResourceWatcher.tick'). */
  operationName: string;
  /** Resource name (e.g. 'borrowers_to_public_data_export_store'). */
  resourceName: string;
  env: DatadogEnv;
  from?: DatadogTimestamp;
  to?: DatadogTimestamp;
}

/**
 * Builds a Datadog APM Resource Page URL.
 *
 * The resource hash is computed via MurmurHash3_x64_128 (first 64 bits),
 * matching Datadog's backend computation.
 */
export function datadogResourceUrl({
  service,
  operationName,
  resourceName,
  env,
  from,
  to,
}: DatadogResourceUrlOptions): string {
  const resourceHash = murmurhash3_resource(resourceName);
  const params = new URLSearchParams({ env });

  if (from != null || to != null) {
    params.set('paused', 'true');
    if (from != null) params.set('start', String(toEpochMs(from)));
    if (to != null) params.set('end', String(toEpochMs(to)));
  }

  return `https://app.datadoghq.com/apm/resource/${encodeURIComponent(service)}/${encodeURIComponent(
    operationName,
  )}/${resourceHash}?${params.toString()}`;
}
